import { gql } from "generated-graphql";

gql(`
  fragment ProcessedManifestRow on ProcessedManifest {
    id
    epaId
    manifestTrackingNumber
    vendor
    shippedDate
    wasteLineCount
    # Container tracking only: drives the reconciliation badge. The resolver
    # answers null without a query when the tenant lacks the flag, so this costs
    # nothing on the tenants that never package shipments.
    linkedShipment {
      id
      manifestRequestNumber
      reconciliation {
        status
      }
    }
    episodicSummary {
      exemptPounds
      episodicEvents {
        id
        reason
      }
    }
    episodicDeclarable
    unresolvedDiscrepancyCount
    pendingDiscrepancyCount
    discrepancyTally {
      complianceTotal
      missingEManifest
      epaCorrection
      epaCorrectionPreviewLines
      fieldTotal
      fieldUnresolved
      fieldPending
      fieldResolved
    }
    latestEpaCorrectionManifestStatus
    vendor
    documentKind
    isEManifest
    isConsolidatedEManifest
    consolidatedEManifest {
      generatorId
      generatorName
      vendorGeneratorId
    }
    isCertified
    eManifestCertifiedDate
    eManifestCreatedDate
    eManifestDaysInTransit
    daysSinceShipment
    eManifestComplianceEscalation
    reportedWasteGeneratorStatus
    isUniformHazardousManifest
    receivedDate
    isRegulatedEManifest
    siteManagerAccess
    desFacilityName
    desFacilityId
    desFacLocationStreet1
    desFacLocationCity
    desFacLocationState
    desFacLocationZip
    transporterContactedAt
    transporterContactedBy
    availableSources
    primarySource
    exceptionReportSubmission {
      id
      title
      status
      filedAt
    }
    facility {
      id
      name
      streetAddress1
      city
      state
      zip
    }
    vendorManifest {
      id
      generatorId
      manifestTrackingNumber
      shippedDate
      vendorSource
      desFacilityName
      desFacilityId
      desFacLocationStreet1
      desFacLocationCity
      desFacLocationState
      desFacLocationZip

      originalManifestTrackingNumber
      originalShippedDate
      originalVendorSource
      originalDesFacilityName

      manuallyEdited
    }
    eManifest {
      id
      generatorId
      generatorTxStateWasteRegistrationId
      shippedDate
      desFacilityName
      desFacilityId
      desFacLocationStreet1
      desFacLocationCity
      desFacLocationState
      desFacLocationZip
    }
    documentManifest {
      id
      generatorId
      generatorTxStateWasteRegistrationId
      shippedDate
      desFacilityName
      desFacilityId
      desFacLocationStreet1
      desFacLocationCity
      desFacLocationState
      desFacLocationZip
    }
    wasteTransporters {
      transporterLineNumber
      transporterName
      transporterId
      daysInPossession
      acknowledgmentDate
      source
      vendorAddressLine1
      vendorAddressLine2
      vendorCity
      vendorState
      vendorZip
    }
    documentStale
    sourceDocument {
      id
      title
      documentType
      originalDocumentType
      fileExtension
      storageLink
      sizeMegabytes
      createdAt
      extractionStatus
      documentMetadata {
        needsReview
        reviewedAt
      }
    }
    documentManifestId
  }
`);

export const PROCESSED_MANIFEST_DOCUMENTS_QUERY = gql(`
  query ProcessedManifestDocuments($search: String) {
    processedManifests(search: $search, page: 0, pageSize: 1) {
      items {
        id
        manifestPdf {
          id
          title
          fileExtension
          storageLink
          documentType
          sizeMegabytes
          createdAt
          extractionStatus
        }
        documents {
          id
          document {
            id
            title
            fileExtension
            documentType
            storageLink
            createdAt
            sizeMegabytes
            extractionStatus
          }
        }
        exceptionReportSubmission {
          id
          title
          documents {
            id
            type
            document {
              id
              title
              fileExtension
              documentType
              storageLink
            }
          }
        }
        additionalManifestDocuments {
          wasteManifestId
          ingestSource
          sourceDocument {
            id
            title
            fileExtension
            storageLink
            documentType
            extractionStatus
            sizeMegabytes
            createdAt
          }
        }
      }
    }
  }
`);

/**
 * Container tracking's two directions for one manifest, for the drawer only.
 *
 * A separate operation rather than fields on `ProcessedManifestRow`: both sides
 * cost a query per manifest, and the row fragment is what the grid renders — a
 * page of 50 manifests would pay for a panel nobody opened. Fired only while the
 * drawer's section is mounted, and only for container-tracking tenants.
 */
export const MANIFEST_SHIPMENT_LINK_QUERY = gql(`
  query ManifestShipmentLink($search: String) {
    processedManifests(search: $search, page: 0, pageSize: 1) {
      items {
        id
        linkedShipment {
          id
          manifestRequestNumber
          status
          # Both dates, as the candidate rows below select: a linked shipment
          # that has not left yet shows when it is planned for, rather than
          # claiming a ship date it does not have.
          plannedShipDate
          shippedDate
          facilityId
          reconciliation {
            status
          }
        }
        linkedContainers {
          id
          containerLabel
          containerType
          amount
          amountUnit
          status
          accumulationArea {
            id
            name
          }
        }
        candidateShipments {
          id
          manifestRequestNumber
          status
          plannedShipDate
          shippedDate
          facilityId
          lines {
            id
          }
        }
      }
    }
  }
`);

export const PROCESSED_MANIFESTS_QUERY = gql(`
  query ProcessedManifests($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    processedManifests(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...ProcessedManifestRow
      }
      count
    }
  }
`);

// Drives the drawer's client-side validation indicators (item 5): fields with
// an Unresolved/Pending discrepancy get a warning icon. No new backend field.
export const MANIFEST_DISCREPANCIES_QUERY = gql(`
  query ManifestDiscrepancies($processedManifestId: ID!) {
    manifestDiscrepancies(processedManifestId: $processedManifestId) {
      id
      field
      status
      kind
      severity
      wasteLineUnifiedId
      eManifestValue
      vendorValue
      manifestDocumentValue
      escalation
    }
  }
`);

export const DELETE_VENDOR_MANIFEST_AND_LINE_ITEMS_MUTATION = gql(`
  mutation DeleteVendorManifestAndLineItems($id: ID!) {
    deleteVendorManifestAndLineItems(id: $id)
  }
`);

export const REVERT_VENDOR_MANIFEST_AND_LINE_ITEMS_MUTATION = gql(`
  mutation RevertVendorManifestAndLineItems($id: ID!) {
    revertVendorManifestAndLineItems(id: $id) {
      originalProcessedManifest {
        id
        manifestTrackingNumber
        shippedDate
        vendor
        desFacilityName
        vendorManifest {
          manifestTrackingNumber
          shippedDate
          vendorSource
          desFacilityName
          manuallyEdited
        }
      }
      newProcessedManifest {
        id
        manifestTrackingNumber
        shippedDate
        vendor
        desFacilityName
        vendorManifest {
          manifestTrackingNumber
          shippedDate
          vendorSource
          desFacilityName
          manuallyEdited
        }
      }
    }
  }
`);

export const DELETE_DOCUMENT_MANIFEST = gql(`
  mutation DeleteDocumentManifest($wasteManifestId: ID!) {
    deleteDocumentManifest(wasteManifestId: $wasteManifestId)
  }
`);

export const UNCONFIRM_BILL_OF_LADING = gql(`
  mutation UnconfirmBillOfLading($wasteManifestId: ID!) {
    unconfirmBillOfLading(wasteManifestId: $wasteManifestId)
  }
`);

export const ATTACH_DOCUMENT_TO_MANIFEST = gql(`
  mutation AttachDocumentToManifest($processedManifestId: ID!, $documentId: ID!) {
    attachDocumentToManifest(processedManifestId: $processedManifestId, documentId: $documentId) {
      id
      document {
        id
        title
        fileExtension
        documentType
        storageLink
        createdAt
      }
    }
  }
`);

export const DETACH_DOCUMENT_FROM_MANIFEST = gql(`
  mutation DetachDocumentFromManifest($processedManifestDocumentId: ID!) {
    detachDocumentFromManifest(processedManifestDocumentId: $processedManifestDocumentId)
  }
`);

export const MARK_TRANSPORTER_CONTACTED = gql(`
  mutation MarkTransporterContacted($processedManifestId: ID!, $contactedBy: String!, $contactedAt: DateTime!) {
    markTransporterContacted(processedManifestId: $processedManifestId, contactedBy: $contactedBy, contactedAt: $contactedAt) {
      id
      transporterContactedAt
      transporterContactedBy
    }
  }
`);

export const CREATE_EXCEPTION_REPORT_SUBMISSION = gql(`
  mutation CreateExceptionReportSubmission($processedManifestId: ID!) {
    createExceptionReportSubmission(processedManifestId: $processedManifestId) {
      id
      title
      status
    }
  }
`);

gql(`
  fragment ShippingPaperworkRow on ShippingPaperworkItem {
    id
    paperworkKind
    documentNumber
    shipDate
    facility {
      id
      name
    }
    # The whole V2 row, not a hand-picked subset. The V3 grid renders V2's own
    # column definitions (useManifestColumns) against the manifest side of each
    # item, so anything V2 selects has to be here or a reused column reads
    # undefined. Spreading the same fragment is what keeps the two lists from
    # drifting apart field by field.
    manifest {
      ...ProcessedManifestRow
    }
    # Resolved per page rather than joined into the view — the link lives on
    # WasteShipment, which is why the column reading this cannot be sorted.
    #
    # Selected to the same shape ProcessedManifestRow already selects
    # linkedShipment in. buildPaperworkRow spreads the manifest and then the
    # item over each other, so a narrower selection here would overwrite the
    # manifest side's richer one and leave the row's own type in conflict.
    linkedShipment {
      id
      manifestRequestNumber
      reconciliation {
        status
      }
    }
  }
`);

/**
 * Manifests and bills of lading interleaved in one page, in the requested
 * sort order. Backs the mixed manifests/bills-of-lading Manifests view.
 * `tenantId` rides the `search` string as a `tenantId:` keyed term, the same
 * convention `processedManifests` uses, rather than its own argument. The
 * schema's `kinds` argument is intentionally not sent here either: the Type
 * filter (`shippingPaperworkKindFilter`) already rides a `kind:` keyed term
 * in `search`, the same way every other omnisearch filter on this grid does,
 * so a second, redundant argument would just be dead weight on the client.
 */
export const SHIPPING_PAPERWORK_QUERY = gql(`
  query ShippingPaperwork($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    shippingPaperwork(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...ShippingPaperworkRow
      }
      count
    }
  }
`);

/**
 * Every query that renders a manifest list, for refetching after a write that
 * can change what belongs in one — a confirm, a detach, a delete.
 *
 * One list per generation (`ProcessedManifests` on V1/V2, `ShippingPaperwork`
 * on V3) plus the Show-preset counts beside them, which move for the same
 * reasons the rows do. Collected here because the write that invalidates them
 * happens in the review dialog, which has no idea which list it was opened
 * from — refreshing only the caller's own list is what left a V3 grid showing
 * a manifest that no longer matched its "needs review" filter.
 *
 * The counts query is named rather than imported to keep this module free of a
 * dependency on the hook that issues it.
 */
export const MANIFEST_LIST_QUERIES = [
  PROCESSED_MANIFESTS_QUERY,
  SHIPPING_PAPERWORK_QUERY,
  "ProcessedManifestShowFilterCounts",
];

export const MANIFEST_DOCUMENT_AUDIT_QUERY = gql(`
  query ManifestDocumentAudit($processedManifestId: ID!) {
    manifestDocumentAudit(processedManifestId: $processedManifestId) {
      documentId
      documentTitle
      uploadedBy
      uploadedAt
      reviewedBy
      reviewedAt
      needsReview
    }
  }
`);
